'use client';
import { Menu as MenuPrimitive } from '@base-ui/react/menu';
import * as React from 'react';

import { FarIcons } from '@playbooks/icons';
import { type ButtonProps, Button } from '@playbooks/ui/components/button';
import { panelElevationVariants } from '@playbooks/ui/components/component-recipes';
import { type IconValue, Icon, resolveIcon } from '@playbooks/ui/components/icon';
import { StructuralIcon } from '@playbooks/ui/components/structural-icon';
import { cn } from '@playbooks/ui/lib/utils';

function DropdownMenu({ ...props }: MenuPrimitive.Root.Props) {
	return <MenuPrimitive.Root data-slot='dropdown-menu' {...props} />;
}
function DropdownMenuPortal({ ...props }: MenuPrimitive.Portal.Props) {
	return <MenuPrimitive.Portal data-slot='dropdown-menu-portal' {...props} />;
}
function DropdownMenuTrigger({ ...props }: MenuPrimitive.Trigger.Props) {
	return <MenuPrimitive.Trigger data-slot='dropdown-menu-trigger' {...props} />;
}
function DropdownMenuContent({
	align = 'start',
	alignOffset = 0,
	side = 'bottom',
	sideOffset = 4,
	collisionAvoidance = { side: 'flip', align: 'shift', fallbackAxisSide: 'none' },
	collisionPadding = 8,
	positionMethod = 'fixed',
	variant = 'menu',
	className,
	onKeyDown,
	...props
}: MenuPrimitive.Popup.Props &
	Pick<
		MenuPrimitive.Positioner.Props,
		'align' | 'alignOffset' | 'side' | 'sideOffset' | 'collisionAvoidance' | 'collisionPadding' | 'positionMethod'
	> & {
		variant?: 'menu' | 'panel';
	}) {
	// Theme
	const rootClassName = cn(
		panelElevationVariants({ elevation: 'flyout', surface: 'default' }),
		'z-flyout grid max-h-(--available-height) max-w-[calc(100vw-1rem)] w-(--anchor-width) min-w-32 origin-(--transform-origin) gap-0.5 overflow-x-hidden overflow-y-auto rounded-panel p-1 duration-fast outline-none data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:overflow-hidden data-closed:fade-out-0 data-closed:zoom-out-95',
		'[&_ul]:grid [&_ul]:gap-0.5',
		variant === 'menu' &&
			'[&_[data-slot=button]:not([data-menu-contained])]:h-8 [&_[data-slot=button]:not([data-menu-contained])]:w-full [&_[data-slot=button]:not([data-menu-contained])]:justify-start [&_[data-slot=button]:not([data-menu-contained])]:border-transparent! [&_[data-slot=button]:not([data-menu-contained])]:bg-transparent! [&_[data-slot=button]:not([data-menu-contained])]:px-2 [&_[data-slot=button]:not([data-menu-contained])]:text-sm [&_[data-slot=button]:not([data-menu-contained])]:text-foreground-body! [&_[data-slot=button]:not([data-menu-contained])]:shadow-none!',
		variant === 'menu' &&
			'[&_[data-slot=button]:not([data-menu-contained]):focus]:bg-surface-hover! [&_[data-slot=button]:not([data-menu-contained]):focus]:text-foreground! [&_[data-slot=button]:not([data-menu-contained]):focus-visible]:border-transparent! [&_[data-slot=button]:not([data-menu-contained]):focus-visible]:ring-0! [&_[data-slot=button]:not([data-menu-contained]):hover]:bg-surface-hover! [&_[data-slot=button]:not([data-menu-contained]):hover]:text-foreground! [&_[data-slot=button]:not([data-menu-contained])[aria-expanded=true]]:bg-surface-hover! [&_[data-slot=button]:not([data-menu-contained])[aria-expanded=true]]:text-foreground! [&_[data-slot=button]:not([data-menu-contained])[data-active=true]]:bg-surface-hover! [&_[data-slot=button]:not([data-menu-contained])[data-active=true]]:text-foreground!',
		className,
	);
	const onPopupKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		onKeyDown?.(event);
		if (event.defaultPrevented || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
		const items = Array.from(
			event.currentTarget.querySelectorAll<HTMLElement>(
				'[role^=menuitem]:not([data-disabled]), [data-slot=button]:not(:disabled):not([aria-disabled=true])',
			),
		).filter(item => item.getClientRects().length > 0);
		const currentIndex = items.indexOf(document.activeElement as HTMLElement);
		if (currentIndex === -1 && event.target !== event.currentTarget) return;
		const nextIndex =
			event.key === 'ArrowDown'
				? currentIndex === -1
					? 0
					: (currentIndex + 1) % items.length
				: currentIndex === -1
					? items.length - 1
					: (currentIndex - 1 + items.length) % items.length;
		if (!items[nextIndex]) return;
		event.preventDefault();
		items[nextIndex].focus();
	};

	// Render
	return (
		<MenuPrimitive.Portal>
			<MenuPrimitive.Positioner
				align={align}
				alignOffset={alignOffset}
				side={side}
				sideOffset={sideOffset}
				collisionAvoidance={collisionAvoidance}
				collisionPadding={collisionPadding}
				positionMethod={positionMethod}
				className='isolate z-flyout outline-none'>
				<MenuPrimitive.Popup
					data-slot='dropdown-menu-content'
					data-variant={variant}
					{...props}
					autoFocus
					tabIndex={-1}
					onKeyDown={onPopupKeyDown}
					className={rootClassName}
				/>
			</MenuPrimitive.Positioner>
		</MenuPrimitive.Portal>
	);
}

function DropdownMenuView({
	direction = 'forward',
	className,
	...props
}: React.ComponentProps<'div'> & { direction?: 'forward' | 'back' }) {
	// Theme
	const rootClassName = cn(
		'w-full min-w-0 max-w-full overflow-x-hidden min-h-48 animate-in fade-in-0 duration-standard data-[direction=back]:slide-in-from-left-2 data-[direction=forward]:slide-in-from-right-2 motion-reduce:animate-none',
		className,
	);

	// Render
	return <div data-slot='dropdown-menu-view' data-direction={direction} className={rootClassName} {...props} />;
}
type DropdownMenuPanelButtonProps = Omit<ButtonProps, 'children' | 'size' | 'variant'> & {
	icon?: IconValue;
	label: React.ReactNode;
	meta?: React.ReactNode;
	variant?: 'default' | 'warning' | 'disabled';
};
function DropdownMenuPanelButton({
	icon,
	label,
	meta,
	variant = 'default',
	className,
	disabled,
	...props
}: DropdownMenuPanelButtonProps) {
	// Theme
	const rootClassName = cn(
		'w-full justify-start rounded-none px-4 text-foreground-body hover:text-foreground',
		variant === 'warning' && 'text-warning hover:text-warning',
		className,
	);

	// Render
	return (
		<Button
			type='button'
			data-slot='dropdown-menu-panel-button'
			variant='ghost'
			size='sm'
			disabled={disabled || variant === 'disabled'}
			className={rootClassName}
			{...props}>
			{icon ? <Icon {...resolveIcon(icon)} className='text-foreground-subtle' aria-hidden /> : null}
			<span className='min-w-0 flex-1 truncate text-left'>{label}</span>
			{meta ? <span className='max-w-28 truncate text-xs text-foreground-subtle'>{meta}</span> : null}
			<StructuralIcon icon={FarIcons.farChevronRight} className='text-foreground-subtle' />
		</Button>
	);
}
function DropdownMenuGroup({ className, ...props }: MenuPrimitive.Group.Props) {
	// Theme
	const rootClassName = cn('grid gap-0.5', className);

	// Render
	return <MenuPrimitive.Group data-slot='dropdown-menu-group' className={rootClassName} {...props} />;
}
function DropdownMenuLabel({
	className,
	inset,
	...props
}: MenuPrimitive.GroupLabel.Props & {
	inset?: boolean;
}) {
	// Theme
	const rootClassName = cn('type-pretitle px-2 py-1 data-inset:pl-7', className);

	// Render
	return (
		<MenuPrimitive.GroupLabel data-slot='dropdown-menu-label' data-inset={inset} {...props} className={rootClassName} />
	);
}
function DropdownMenuItem({
	className,
	inset,
	variant = 'default',
	...props
}: MenuPrimitive.Item.Props & {
	inset?: boolean;
	variant?: 'default' | 'destructive';
}) {
	// Theme
	const rootClassName = cn(
		"group/dropdown-menu-item relative flex cursor-default items-center gap-2.5 rounded-panel px-2 py-1.5 text-sm text-foreground-body outline-hidden select-none focus:bg-surface-hover focus:text-foreground focus:**:text-foreground data-inset:pl-7 data-[variant=destructive]:text-danger data-[variant=destructive]:focus:bg-danger/10 data-[variant=destructive]:focus:text-danger dark:data-[variant=destructive]:focus:bg-danger/20 data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3 data-[variant=destructive]:*:[svg]:text-danger",
		className,
	);

	// Render
	return (
		<MenuPrimitive.Item
			data-slot='dropdown-menu-item'
			data-inset={inset}
			data-variant={variant}
			{...props}
			className={rootClassName}
		/>
	);
}
function DropdownMenuSub({ ...props }: MenuPrimitive.SubmenuRoot.Props) {
	return <MenuPrimitive.SubmenuRoot data-slot='dropdown-menu-sub' {...props} />;
}
function DropdownMenuSubTrigger({
	className,
	inset,
	children,
	...props
}: MenuPrimitive.SubmenuTrigger.Props & {
	inset?: boolean;
}) {
	// Theme
	const rootClassName = cn(
		"flex cursor-default items-center gap-2.5 rounded-panel px-2 py-1.5 text-sm text-foreground-body outline-hidden select-none focus:bg-surface-hover focus:text-foreground focus:**:text-foreground data-inset:pl-7 data-popup-open:bg-surface-hover data-popup-open:text-foreground data-open:bg-surface-hover data-open:text-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3",
		className,
	);

	// Render
	return (
		<MenuPrimitive.SubmenuTrigger
			data-slot='dropdown-menu-sub-trigger'
			data-inset={inset}
			{...props}
			className={rootClassName}>
			{children}
			<StructuralIcon icon={FarIcons.farChevronRight} className='ml-auto' />
		</MenuPrimitive.SubmenuTrigger>
	);
}
function DropdownMenuSubContent({
	align = 'start',
	alignOffset = -3,
	side = 'right',
	sideOffset = 0,
	className,
	...props
}: React.ComponentProps<typeof DropdownMenuContent>) {
	// Theme
	const rootClassName = cn(
		panelElevationVariants({ elevation: 'flyout', surface: 'default' }),
		'w-auto min-w-[96px] rounded-panel p-1 duration-fast data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
		className,
	);

	// Render
	return (
		<DropdownMenuContent
			data-slot='dropdown-menu-sub-content'
			align={align}
			alignOffset={alignOffset}
			side={side}
			sideOffset={sideOffset}
			{...props}
			className={rootClassName}
		/>
	);
}
function DropdownMenuCheckboxItem({
	className,
	children,
	checked,
	inset,
	...props
}: MenuPrimitive.CheckboxItem.Props & {
	inset?: boolean;
}) {
	// Theme
	const rootClassName = cn(
		"relative flex cursor-default items-center gap-2.5 rounded-panel py-1.5 pr-8 pl-2 text-sm text-foreground-body outline-hidden select-none focus:bg-surface-hover focus:text-foreground focus:**:text-foreground data-inset:pl-7 data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3",
		className,
	);

	// Render
	return (
		<MenuPrimitive.CheckboxItem
			data-slot='dropdown-menu-checkbox-item'
			data-inset={inset}
			checked={checked}
			{...props}
			className={rootClassName}>
			<span
				data-slot='dropdown-menu-checkbox-item-indicator'
				className='pointer-events-none absolute right-2 flex items-center justify-center'>
				<MenuPrimitive.CheckboxItemIndicator>
					<StructuralIcon icon={FarIcons.farCheck} />
				</MenuPrimitive.CheckboxItemIndicator>
			</span>
			{children}
		</MenuPrimitive.CheckboxItem>
	);
}
function DropdownMenuRadioGroup({ ...props }: MenuPrimitive.RadioGroup.Props) {
	return <MenuPrimitive.RadioGroup data-slot='dropdown-menu-radio-group' {...props} />;
}
function DropdownMenuRadioItem({
	className,
	children,
	inset,
	...props
}: MenuPrimitive.RadioItem.Props & {
	inset?: boolean;
}) {
	// Theme
	const rootClassName = cn(
		"relative flex cursor-default items-center gap-2.5 rounded-panel py-1.5 pr-8 pl-2 text-sm text-foreground-body outline-hidden select-none focus:bg-surface-hover focus:text-foreground focus:**:text-foreground data-inset:pl-7 data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3",
		className,
	);

	// Render
	return (
		<MenuPrimitive.RadioItem
			data-slot='dropdown-menu-radio-item'
			data-inset={inset}
			{...props}
			className={rootClassName}>
			<span
				data-slot='dropdown-menu-radio-item-indicator'
				className='pointer-events-none absolute right-2 flex items-center justify-center'>
				<MenuPrimitive.RadioItemIndicator>
					<StructuralIcon icon={FarIcons.farCheck} />
				</MenuPrimitive.RadioItemIndicator>
			</span>
			{children}
		</MenuPrimitive.RadioItem>
	);
}
function DropdownMenuSeparator({ className, ...props }: MenuPrimitive.Separator.Props) {
	// Theme
	const rootClassName = cn('-mx-1 my-1 h-px bg-border', className);

	// Render
	return <MenuPrimitive.Separator data-slot='dropdown-menu-separator' {...props} className={rootClassName} />;
}
function DropdownMenuShortcut({ className, ...props }: React.ComponentProps<'span'>) {
	// Theme
	const rootClassName = cn(
		'ml-auto text-xs tracking-widest text-foreground-body group-focus/dropdown-menu-item:text-foreground',
		className,
	);

	// Render
	return <span data-slot='dropdown-menu-shortcut' {...props} className={rootClassName} />;
}
export {
	DropdownMenu,
	DropdownMenuPortal,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuLabel,
	DropdownMenuItem,
	DropdownMenuPanelButton,
	DropdownMenuView,
	DropdownMenuCheckboxItem,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuSub,
	DropdownMenuSubTrigger,
	DropdownMenuSubContent,
};
